<template>
  <div class="db-demo">
    <aside class="db-demo__sidebar">
      <h5 class="db-demo__title">Document Builder — Demo</h5>

      <label class="db-demo__label">Nombre del documento</label>
      <input
        v-model="docName"
        class="db-demo__input"
        type="text"
        placeholder="Nombre del documento"
      />

      <label class="db-demo__label">Importar HTML</label>
      <textarea
        v-model="htmlInput"
        class="db-demo__textarea"
        rows="5"
        placeholder="Pega aquí el HTML del documento..."
      ></textarea>
      <b-button
        class="db-demo__import-btn"
        size="sm"
        variant="primary"
        :disabled="!htmlInput.trim()"
        @click="openImportModal"
      >
        Importar HTML al lienzo
      </b-button>

      <!-- Pregunta qué hacer con los estilos del HTML importado. -->
      <b-modal
        v-model="showImportModal"
        title="Importar HTML"
        centered
        hide-footer
      >
        <p class="db-demo__modal-text">
          ¿Qué quieres hacer con los estilos (CSS) al importar este HTML?
        </p>
        <div class="db-demo__modal-actions">
          <b-button variant="primary" @click="confirmImport('merge')">
            Fusionar
            <small class="db-demo__modal-hint">
              Combina el CSS actual con el del HTML importado
            </small>
          </b-button>
          <b-button variant="outline-primary" @click="confirmImport('replace')">
            Reemplazar
            <small class="db-demo__modal-hint">
              Usa solo el CSS del HTML importado
            </small>
          </b-button>
          <b-button variant="outline-primary" @click="confirmImport('keep')">
            Mantener
            <small class="db-demo__modal-hint">
              Conserva el CSS actual e ignora el del HTML
            </small>
          </b-button>
        </div>
      </b-modal>

      <label class="db-demo__label">Tamaño de página</label>
      <div class="db-demo__sizes">
        <b-button
          size="sm"
          :variant="currentPageSize === 'A4' ? 'primary' : 'outline-primary'"
          @click="setPageSize('A4')"
        >
          A4
        </b-button>
        <b-button
          size="sm"
          :variant="currentPageSize === 'CARTA' ? 'primary' : 'outline-primary'"
          @click="setPageSize('CARTA')"
        >
          Carta
        </b-button>
      </div>

      <label class="db-demo__label">Acciones</label>
      <div class="db-demo__actions">
        <b-button size="sm" variant="outline-primary" @click="exportHtml">
          Exportar HTML
        </b-button>
        <b-button size="sm" variant="outline-success" @click="saveJson">
          Guardar JSON
        </b-button>
        <b-button
          size="sm"
          variant="outline-warning"
          :disabled="!savedJson"
          @click="loadJson"
        >
          Cargar JSON
        </b-button>
        <b-button size="sm" variant="outline-info" @click="openCodeModal">
          Editar código (HTML/CSS)
        </b-button>
        <b-button size="sm" variant="outline-dark" @click="printA4">
          Imprimir / Preview A4
        </b-button>
      </div>

      <label class="db-demo__label">Salida (HTML/CSS o JSON del proyecto)</label>
      <pre class="db-demo__json">{{ projectJson }}</pre>

      <!-- Editor de código GLOBAL del documento, con pestañas HTML y CSS (patrón
           "Editor online" del repo, con resaltado via CodeEditor/prism). -->
      <b-modal
        v-model="showCodeModal"
        title="Editor de código"
        size="xl"
        centered
        ok-title="Aplicar"
        cancel-title="Cancelar"
        @ok="applyCode"
      >
        <b-tabs>
          <b-tab title="HTML" active>
            <CodeEditor v-model="codeHtml" />
          </b-tab>
          <b-tab title="CSS">
            <CodeEditor v-model="codeCss" />
          </b-tab>
        </b-tabs>
      </b-modal>

      <!-- Editor de código POR COMPONENTE: HTML crudo del elemento seleccionado,
           abierto desde el botón "</>" de la toolbar del componente. -->
      <b-modal
        v-model="showComponentCodeModal"
        title="Editar HTML del componente"
        size="xl"
        centered
        ok-title="Aplicar"
        cancel-title="Cancelar"
        @ok="applyComponentCode"
      >
        <CodeEditor v-model="componentHtml" />
      </b-modal>
    </aside>

    <main class="db-demo__main">
      <GrapesDocEditor
        ref="editor"
        :initial-page-size="currentPageSize"
        @edit-component-code="openComponentCodeModal"
      />
    </main>
  </div>
</template>

<script>

import GrapesDocEditor from '@/febos/global/customDocuments/docBuilder/components/GrapesDocEditor';
// Editor de código con resaltado reutilizado del repo (mismo que usa s3-html);
// envuelve vue-prism-editor + prismjs. v-model via prop value + evento input.
import CodeEditor from '@/febos/global/empresas/componentes/configs/inputs/CodeEditor';

export default {
  name: 'DocBuilderDemo',
  components: { GrapesDocEditor, CodeEditor },
  data() {
    return {
      docName: 'Documento sin título',
      savedJson: null,
      htmlInput: '',
      showImportModal: false,
      output: '',
      currentPageSize: 'A4',
      // Editor de código global (pestañas HTML/CSS).
      showCodeModal: false,
      codeHtml: '',
      codeCss: '',
      // Editor de código por componente (HTML crudo del seleccionado).
      showComponentCodeModal: false,
      componentHtml: '',
    };
  },
  computed: {
    projectJson() {
      return this.output || 'Usa los botones para exportar HTML/CSS o guardar el JSON del proyecto.';
    },
  },
  methods: {
    // Cambia el tamaño de página activo (A4/CARTA) y lo aplica en el editor.
    setPageSize(sizeKey) {
      this.currentPageSize = sizeKey;
      this.$refs.editor.setPageSize(sizeKey);
      const labels = { A4: 'A4', CARTA: 'Carta' };
      this.output = `Tamaño de página: ${labels[sizeKey] || sizeKey}.`;
    },
    // Abre el modal que pregunta qué hacer con los CSS al importar.
    openImportModal() {
      if (!this.htmlInput.trim()) return;
      this.showImportModal = true;
    },
    // Importa el HTML pegado con el modo de CSS elegido en el modal
    // ('merge' | 'replace' | 'keep') y cierra el modal.
    confirmImport(cssMode) {
      const html = this.htmlInput.trim();
      if (!html) return;
      this.$refs.editor.importHtml(html, cssMode);
      this.showImportModal = false;
      const labels = { merge: 'fusionado', replace: 'reemplazado', keep: 'mantenido' };
      this.output = `HTML importado (CSS ${labels[cssMode] || cssMode}).`;
    },
    // Exporta HTML + CSS del documento (sin backend, API de core del editor).
    exportHtml() {
      const { html, css } = this.$refs.editor.exportHtmlCss();
      this.output = `${html}\n\n/* ===== CSS ===== */\n${css}`;
    },
    // Guarda el JSON del proyecto en memoria y lo muestra en el panel.
    saveJson() {
      this.savedJson = this.$refs.editor.getProjectData();
      this.output = JSON.stringify(this.savedJson, null, 2);
    },
    // Round-trip puro: recarga el editor con el JSON guardado por saveJson().
    loadJson() {
      if (!this.savedJson) return;
      this.$refs.editor.loadProjectData(this.savedJson);
      this.output = 'Proyecto recargado desde el JSON guardado (loadProjectData).';
    },
    // Abre el editor de código GLOBAL, precargando HTML y CSS actuales del documento.
    openCodeModal() {
      this.codeHtml = this.$refs.editor.getHtml();
      this.codeCss = this.$refs.editor.getCss();
      this.showCodeModal = true;
    },
    // Aplica el HTML y el CSS editados en el modal global (ambas pestañas).
    applyCode() {
      this.$refs.editor.applyHtml(this.codeHtml);
      this.$refs.editor.applyCss(this.codeCss);
      this.output = 'Código HTML/CSS aplicado al documento.';
    },
    // Abre el editor de código POR COMPONENTE con el HTML emitido por el editor
    // (evento edit-component-code disparado por el botón "</>" de la toolbar).
    openComponentCodeModal(html) {
      this.componentHtml = html;
      this.showComponentCodeModal = true;
    },
    // Reemplaza el HTML del componente seleccionado por el editado.
    applyComponentCode() {
      this.$refs.editor.applyComponentHtml(this.componentHtml);
      this.output = 'HTML del componente actualizado.';
    },
    // Imprime/previsualiza el documento en A4 (ventana aislada con CSS @page).
    printA4() {
      this.$refs.editor.printA4();
    },
  },
};

</script>

<style lang="scss" scoped>

.db-demo {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.db-demo__sidebar {
  width: 340px;
  min-width: 340px;
  padding: 16px;
  background: #f8f8fb;
  border-right: 1px solid #e3e3ef;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.db-demo__title {
  margin-bottom: 12px;
}

.db-demo__label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b85;
  margin: 10px 0 4px;
}

.db-demo__input {
  padding: 4px 8px;
  border: 1px solid #d8d8e3;
  border-radius: 4px;
  font-size: 12px;
}

.db-demo__textarea {
  padding: 6px 8px;
  border: 1px solid #d8d8e3;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  resize: vertical;
}

.db-demo__import-btn {
  margin-top: 6px;
}

.db-demo__modal-text {
  font-size: 13px;
  margin-bottom: 12px;
}

.db-demo__modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.db-demo__modal-actions > * {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.db-demo__modal-hint {
  font-weight: 400;
  opacity: 0.85;
  font-size: 11px;
}

.db-demo__sizes {
  display: flex;
  gap: 6px;
}

.db-demo__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.db-demo__json {
  background: #1e1e2d;
  color: #c3e88d;
  font-size: 11px;
  padding: 10px;
  border-radius: 4px;
  overflow: auto;
  max-height: 320px;
  white-space: pre-wrap;
  word-break: break-word;
}

.db-demo__main {
  flex: 1;
  display: flex;
  overflow: hidden;
}

</style>
